import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { Breadcrumb } from '@/components/ui/breadcrumb'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  AppShell,
  getSettingsNav,
  SETTINGS_NAV_HREF
} from '@/domains/app-shell'
import { OnboardingShell, useSetupState } from '@/domains/setup'

/** shared shell for every settings sub-route. section navigation lives in the
 * app sidebar under Settings, so this adds only the breadcrumb, the heading and
 * the phone section picker. mid-onboarding the account settings are reachable
 * but the full app nav is not, so fall back to the OnboardingShell to keep the
 * user in that context instead of surfacing a product nav that only bounces
 * back.
 */
export function SettingsLayout({
  title,
  description,
  children
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  const { setupState } = useSetupState()
  const Shell = setupState?.isOnboarding ? OnboardingShell : AppShell

  return (
    <Shell
      title={
        <Breadcrumb
          items={[
            { label: 'Settings', to: SETTINGS_NAV_HREF },
            { label: title }
          ]}
        />
      }
    >
      <div className='flex w-full flex-col gap-5'>
        <div>
          <h1 className='font-display text-[22px] leading-[1.1] tracking-[-0.4px] text-ink sm:text-[26px]'>
            {title}
          </h1>
          {description && (
            <p className='mt-1 text-[13px] text-ink-soft'>{description}</p>
          )}
        </div>
        <SectionSwitcher />
        {children}
      </div>
    </Shell>
  )
}

/* the sidebar is the only section picker, and on a phone it sits behind the
   drawer plus an expand */
function SectionSwitcher() {
  const { setupState } = useSetupState()
  const navigate = useNavigate()
  const location = useLocation()
  if (!setupState) return null

  const items = getSettingsNav(setupState).flatMap(group => group.items)
  if (items.length < 2) return null

  const current =
    items.find(item => item.href === location.pathname)?.href ?? items[0]!.href

  return (
    <Select value={current} onValueChange={href => void navigate(href)}>
      <SelectTrigger aria-label='Settings section' className='sm:hidden'>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map(item => (
          <SelectItem key={item.href} value={item.href}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
